import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoginService } from 'src/app/login/login.service';
import { OrganizationService } from 'src/app/shared/organization.service';
import { PermissionsService } from 'src/app/shared/permissions/permissions.service';
import { SettingsService } from 'src/app/settings/settings.service';
import { TeacherOnboardingService } from 'src/app/teacher-onboarding/teacher-onboarding.service';
import { cancellable } from 'src/app/shared/cancellable.util';
import { HelpWorkflow } from '../help-context.model';
import { HelpContextService } from '../help-context.service';

/**
 * The floating help button: the workflows available on whatever the teacher is
 * looking at, and the steps to get each one done.
 *
 * Mounted once beside the navbar, so it survives navigation and dialogs. It
 * shows nothing at all unless the current screen has registered help -- an
 * always-visible button that usually answers "no guidance here" teaches people
 * to stop pressing it.
 */
@Component({
  selector: 'app-help-fab',
  templateUrl: './help-fab.component.html',
  styleUrls: ['./help-fab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HelpFabComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  open = false;
  /** The workflow whose steps are showing, or null for the workflow list. */
  selected: HelpWorkflow | null = null;

  @ViewChild('panel') private panelRef?: ElementRef<HTMLElement>;
  @ViewChild('fab') private fabRef?: ElementRef<HTMLElement>;

  private enabled = false;

  constructor(
    private help: HelpContextService,
    private permissions: PermissionsService,
    private loginService: LoginService,
    private organizationService: OrganizationService,
    private settings: SettingsService,
    private onboarding: TeacherOnboardingService,
  ) {}

  async ngOnInit(): Promise<void> {
    // Moving between screens changes what the panel would be describing, so it
    // closes rather than silently showing the new screen's workflows.
    this.help.current$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.close();
    });

    if (!this.isTeamMember) return;
    // Seeded from the userInfo cookie so the first paint already knows whether
    // the onboarding checklist owns the corner. Refreshing it is that FAB's job.
    this.onboarding.seedFromUserInfo();
    try {
      const config = await cancellable(
        this.settings.getSystemConfig(this.organizationService.getOrganizationId()),
        this.destroyRef,
      );
      this.enabled = config.helpFabEnabled;
    } catch {
      this.enabled = false;
    }
  }

  private get isTeamMember(): boolean {
    try {
      return this.loginService.userInfo?.type === 'team';
    } catch {
      return false;
    }
  }

  /** Workflows this teacher may actually perform, in authored order. */
  get workflows(): HelpWorkflow[] {
    const context = this.help.current;
    if (!context) return [];
    return context.workflows.filter(
      (workflow) => !workflow.permission || this.permissions.has(workflow.permission),
    );
  }

  get title(): string {
    return this.help.current?.title ?? '';
  }

  get subtitle(): string | undefined {
    return this.help.current?.subtitle;
  }

  get visible(): boolean {
    return (
      this.enabled &&
      this.isTeamMember &&
      // One corner, one button: setup comes before page help.
      !this.onboarding.isChecklistVisible() &&
      this.workflows.length > 0
    );
  }

  toggle(): void {
    this.open ? this.dismiss() : this.openPanel();
  }

  private openPanel(): void {
    this.open = true;
    this.selected = null;
    // The panel is rendered by the same change detection pass that this click
    // triggers, so the element only exists on the next tick.
    setTimeout(() => this.panelRef?.nativeElement.focus());
  }

  /** Closed by the teacher: focus goes back to the button they came from. */
  dismiss(): void {
    if (!this.open) return;
    this.close();
    setTimeout(() => this.fabRef?.nativeElement.focus());
  }

  /**
   * Closed by something other than the teacher -- navigating away, a dialog
   * opening. Focus is left alone: it belongs to whatever caused the change,
   * and pulling it onto the help button would hijack the new screen.
   */
  close(): void {
    this.open = false;
    this.selected = null;
  }

  select(workflow: HelpWorkflow): void {
    this.selected = workflow;
    setTimeout(() => this.panelRef?.nativeElement.focus());
  }

  back(): void {
    this.selected = null;
    setTimeout(() => this.panelRef?.nativeElement.focus());
  }

  /**
   * F1 opens the panel from anywhere, including inside a dialog whose focus trap
   * a keyboard user cannot Tab out of to reach the button itself.
   */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'F1' && this.visible) {
      event.preventDefault();
      this.toggle();
      return;
    }
    if (event.key === 'Escape' && this.open) {
      event.preventDefault();
      this.dismiss();
    }
  }
}
